import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Navigate } from "react-router-dom";
import {
  FiActivity,
  FiCalendar,
  FiCheckCircle,
  FiClock,
  FiEdit,
  FiFilter,
  FiPlusCircle,
  FiRefreshCw,
  FiTrash2,
  FiUser,
  FiXCircle,
} from "react-icons/fi";
import { toast } from "react-toastify";
import BackButton from "../components/BackButton";
import LoadingOverlay from "../components/LoadingOverlay";
import PaginationControls from "../components/PaginationControls";
import { apiURL } from "../config/config";
import { isAdmin } from "../utils/buttonVisibility";

const PAGE_SIZE = 10;
const ACTION_OPTIONS = [
  { value: "create", label: "Create" },
  { value: "edit", label: "Edit" },
  { value: "delete", label: "Delete" },
  { value: "approve", label: "Approve" },
  { value: "fully_approve", label: "Fully Approve" },
  { value: "reject", label: "Reject" },
];

const normalizeLogs = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.logs)) return payload.logs;
  if (Array.isArray(payload?.records)) return payload.records;
  return [];
};

const buildActionQuery = (actions) =>
  actions
    .map((action) => String(action || "").trim())
    .filter(Boolean)
    .join(",");

const formatDateTime = (value) => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleString("th-TH", {
    dateStyle: "medium",
    timeStyle: "short",
  });
};

const getActionMeta = (action) => {
  const normalizedAction = String(action || "").trim().toLowerCase();

  switch (normalizedAction) {
    case "create":
      return {
        label: "Create",
        icon: <FiPlusCircle size={16} />,
        badgeClass:
          "bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300",
      };
    case "edit":
      return {
        label: "Edit",
        icon: <FiEdit size={16} />,
        badgeClass:
          "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
      };
    case "delete":
      return {
        label: "Delete",
        icon: <FiTrash2 size={16} />,
        badgeClass:
          "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300",
      };
    case "approve":
      return {
        label: "Approve",
        icon: <FiCheckCircle size={16} />,
        badgeClass:
          "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
      };
    case "fully_approve":
      return {
        label: "Fully Approve",
        icon: <FiCheckCircle size={16} />,
        badgeClass:
          "bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300",
      };
    case "reject":
      return {
        label: "Reject",
        icon: <FiXCircle size={16} />,
        badgeClass:
          "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
      };
    default:
      return {
        label: normalizedAction || "-",
        icon: <FiActivity size={16} />,
        badgeClass:
          "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200",
      };
  }
};

const TravelExpenseLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    action: [],
    performedBy: "",
    startDate: "",
    endDate: "",
    keyword: "",
  });

  const admin = useMemo(() => isAdmin(), []);
  const serverFilters = useMemo(
    () => ({
      action: filters.action,
      performedBy: filters.performedBy,
      startDate: filters.startDate,
      endDate: filters.endDate,
    }),
    [filters.action, filters.endDate, filters.performedBy, filters.startDate]
  );

  const fetchLogs = useCallback(async (nextFilters) => {
    try {
      setLoading(true);

      const token = localStorage.getItem("token");
      const params = {};
      const actionQuery = buildActionQuery(nextFilters.action);

      if (actionQuery) params.action = actionQuery;
      if (nextFilters.performedBy.trim()) {
        params.performedBy = nextFilters.performedBy.trim();
      }
      if (nextFilters.startDate) params.startDate = nextFilters.startDate;
      if (nextFilters.endDate) params.endDate = nextFilters.endDate;

      const response = await axios.get(`${apiURL}travel-expenses/logs`, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const normalizedLogs = normalizeLogs(response.data).sort(
        (a, b) =>
          new Date(
            b?.performedAt || b?.timestamp || b?.createdAt || b?.updatedAt || 0
          ) -
          new Date(
            a?.performedAt || a?.timestamp || a?.createdAt || a?.updatedAt || 0
          )
      );

      setLogs(normalizedLogs);
      setCurrentPage(1);
    } catch (error) {
      console.error("Fetch travel expense logs error:", error);
      toast.error(
        error?.response?.data?.message ||
          "ไม่สามารถโหลดประวัติการทำรายการเบิกค่าเดินทางได้"
      );
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (admin) {
      fetchLogs(serverFilters);
    }
  }, [admin, fetchLogs, serverFilters]);

  const filteredLogs = useMemo(() => {
    const keyword = filters.keyword.trim().toLowerCase();

    if (!keyword) return logs;

    return logs.filter((log) => {
      const searchableValues = [
        log.travelExpenseId,
        log.travelExpense?._id,
        log.recordId,
        log.description,
        log.details,
        log.message,
      ];

      return searchableValues.some((value) =>
        String(value || "").toLowerCase().includes(keyword)
      );
    });
  }, [filters.keyword, logs]);

  const totalPages = Math.max(1, Math.ceil(filteredLogs.length / PAGE_SIZE));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginatedLogs = useMemo(() => {
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    return filteredLogs.slice(startIndex, startIndex + PAGE_SIZE);
  }, [currentPage, filteredLogs]);

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleActionToggle = (action) => {
    setFilters((prev) => {
      const exists = prev.action.includes(action);
      return {
        ...prev,
        action: exists
          ? prev.action.filter((item) => item !== action)
          : [...prev.action, action],
      };
    });
  };

  const handleApplyFilters = () => {
    fetchLogs(filters);
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      action: [],
      performedBy: "",
      startDate: "",
      endDate: "",
      keyword: "",
    };

    setFilters(clearedFilters);
    fetchLogs(clearedFilters);
  };

  if (!admin) {
    return <Navigate to="/travel-expense" replace />;
  }

  return (
    <div className="px-4 sm:px-6 py-6 relative min-h-screen bg-gray-50 dark:bg-gray-950 transition-colors duration-300">
      <LoadingOverlay loading={loading} />

      <div className="mb-6">
        <div className="flex items-center justify-between gap-3 mb-4">
          <BackButton to="/travel-expense" label="กลับหน้า Travel Expense" />
        </div>

        <div className="rounded-3xl border border-sky-100 dark:border-sky-900/40 bg-gradient-to-br from-white via-sky-50/70 to-cyan-50/70 dark:from-gray-900 dark:via-gray-900 dark:to-gray-950 p-6 sm:p-8 shadow-sm">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-sky-100 dark:bg-sky-900/30 flex items-center justify-center text-sky-700 dark:text-sky-300 shrink-0">
              <FiActivity size={22} />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900 dark:text-gray-100">
                Travel Expense Logs
              </h1>
              <p className="mt-2 text-sm sm:text-base text-gray-600 dark:text-gray-400 max-w-3xl">
                ตรวจสอบประวัติการแก้ไข อนุมัติ ปฏิเสธ หรือลบรายการเบิกค่าเดินทาง
                พร้อมค้นหาย้อนหลังตามผู้ดำเนินการ ช่วงวันที่ และเลขรายการเบิก
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-4 mb-6">
        <div className="rounded-2xl border border-white/70 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Total Logs
          </div>
          <div className="mt-2 text-2xl font-semibold text-gray-900 dark:text-gray-100">
            {logs.length}
          </div>
        </div>
        <div className="rounded-2xl border border-white/70 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Selected Actions
          </div>
          <div className="mt-2 text-2xl font-semibold text-gray-900 dark:text-gray-100">
            {filters.action.length || "All"}
          </div>
        </div>
        <div className="rounded-2xl border border-white/70 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Performed By
          </div>
          <div className="mt-2 text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
            {filters.performedBy || "All users"}
          </div>
        </div>
        <div className="rounded-2xl border border-white/70 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Keyword
          </div>
          <div className="mt-2 text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
            {filters.keyword || "All records"}
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 shadow-sm mb-6">
        <div className="flex items-center gap-2 mb-4">
          <FiFilter className="text-sky-600 dark:text-sky-400" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Filters
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          <label className="space-y-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Performed By
            </span>
            <div className="relative">
              <FiUser className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={filters.performedBy}
                onChange={handleInputChange("performedBy")}
                placeholder="เช่น manager01"
                className="w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 pl-10 pr-3 py-2.5 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-sky-400"
              />
            </div>
          </label>

          <label className="space-y-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Keyword
            </span>
            <input
              type="text"
              value={filters.keyword}
              onChange={handleInputChange("keyword")}
              placeholder="Search ID, description, details"
              className="w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 px-3 py-2.5 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-sky-400"
            />
          </label>

          <label className="space-y-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Start Date
            </span>
            <div className="relative">
              <FiCalendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="date"
                value={filters.startDate}
                onChange={handleInputChange("startDate")}
                className="w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 pl-10 pr-3 py-2.5 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-sky-400"
              />
            </div>
          </label>

          <label className="space-y-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              End Date
            </span>
            <div className="relative">
              <FiCalendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="date"
                value={filters.endDate}
                onChange={handleInputChange("endDate")}
                className="w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 pl-10 pr-3 py-2.5 text-sm text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-sky-400"
              />
            </div>
          </label>
        </div>

        <div className="mt-5">
          <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Actions
          </div>
          <div className="flex flex-wrap gap-2">
            {ACTION_OPTIONS.map((option) => {
              const active = filters.action.includes(option.value);
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleActionToggle(option.value)}
                  className={[
                    "rounded-full border px-3 py-1.5 text-sm font-medium transition-colors",
                    active
                      ? "border-sky-500 bg-sky-500 text-white"
                      : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-700 dark:text-gray-300 hover:border-sky-300 dark:hover:border-sky-700",
                  ].join(" ")}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={handleApplyFilters}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-xl bg-sky-600 hover:bg-sky-700 px-4 py-2.5 text-sm font-medium text-white transition disabled:opacity-60"
          >
            <FiFilter />
            Apply Filters
          </button>
          <button
            type="button"
            onClick={() => fetchLogs(filters)}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-200 transition hover:border-sky-300 dark:hover:border-sky-700 disabled:opacity-60"
          >
            <FiRefreshCw className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
          <button
            type="button"
            onClick={handleClearFilters}
            className="inline-flex items-center gap-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-200 transition hover:border-rose-300 dark:hover:border-rose-700"
          >
            <FiXCircle />
            Clear
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-gray-600 dark:text-gray-200">
                  #
                </th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600 dark:text-gray-200">
                  Action
                </th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600 dark:text-gray-200">
                  Performed By
                </th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600 dark:text-gray-200">
                  Travel Expense ID
                </th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600 dark:text-gray-200">
                  Details
                </th>
                <th className="px-4 py-3 text-left font-semibold text-gray-600 dark:text-gray-200">
                  Timestamp
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedLogs.length > 0 ? (
                paginatedLogs.map((log, index) => {
                  const actionMeta = getActionMeta(log.action);
                  const rowNumber = (currentPage - 1) * PAGE_SIZE + index + 1;
                  const travelExpenseId =
                    log.travelExpenseId ||
                    log.travelExpense?._id ||
                    log.recordId ||
                    "-";

                  return (
                    <tr
                      key={log._id || `${travelExpenseId}-${rowNumber}`}
                      className="border-b border-gray-100 dark:border-gray-800 hover:bg-sky-50/60 dark:hover:bg-sky-900/10 transition-colors"
                    >
                      <td className="px-4 py-4 text-gray-600 dark:text-gray-300 align-top">
                        {rowNumber}
                      </td>
                      <td className="px-4 py-4 align-top">
                        <span
                          className={[
                            "inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold",
                            actionMeta.badgeClass,
                          ].join(" ")}
                        >
                          {actionMeta.icon}
                          {actionMeta.label}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-gray-700 dark:text-gray-200 align-top">
                        {log.performedBy || "-"}
                      </td>
                      <td className="px-4 py-4 text-gray-600 dark:text-gray-300 align-top font-mono text-xs sm:text-sm">
                        {travelExpenseId}
                      </td>
                      <td className="px-4 py-4 text-gray-700 dark:text-gray-300 align-top">
                        <div className="max-w-xl whitespace-pre-wrap break-words">
                          {log.description || log.details || log.message || "-"}
                        </div>
                      </td>
                      <td className="px-4 py-4 align-top">
                        <div className="inline-flex items-center gap-2 text-gray-600 dark:text-gray-300">
                          <FiClock className="shrink-0" />
                          <span>
                            {formatDateTime(
                              log.performedAt ||
                                log.timestamp ||
                                log.createdAt ||
                                log.updatedAt
                            )}
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan="6"
                    className="px-4 py-10 text-center text-gray-500 dark:text-gray-400"
                  >
                    ไม่พบข้อมูลประวัติการทำรายการตามเงื่อนไขที่เลือก
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <PaginationControls
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </div>
  );
};

export default TravelExpenseLogs;
